/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { UnitOfMeasureData } from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import { Clickable, Icon, LoadingSpinner } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { NamedExoticComponent, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    extendedAdditionalStyles?: AddToCartButtonStyles;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    iconOnly?: boolean;
    alwaysVisible?: boolean;
    asLink?: boolean;
    withIcon?: boolean;
    iconColor?: string;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    ButtonPresentationProps;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export interface AddToCartButtonStyles {
    asButton?: ButtonPresentationProps;
    asLink?: ClickablePresentationProps;
    asLinkIconTextWrapper?: InjectableCss;
    asLinkIcon?: IconPresentationProps;
    asLinkText?: TypographyPresentationProps;
}

export const productAddToCartButtonStyles: AddToCartButtonStyles = {
    asButton: {
        css: css`
            &.button-wrapper-icon-only {
                // this is for button icon only
                display: flex;
                align-items: center;
                width: 48px;
                height: ${(props: any) => (props._sizeVariant === "small" ? "32px" : "40px")};
                min-height: ${(props: any) => (props._sizeVariant === "small" ? "32px" : "40px")};
                justify-content: center;
                padding: 0;
            }

            &:not(.button-wrapper-icon-only) {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding-left: 0;
                padding-right: 0;
                text-transform: uppercase;
                height: ${(props: any) => (props._sizeVariant === "small" ? "32px" : "40px")};
                min-height: ${(props: any) => (props._sizeVariant === "small" ? "32px" : "40px")};
                min-width: ${(props: any) => (props._sizeVariant === "small" ? "140px" : "180px")};
                > span:not(.atc-label) {
                    width: ${(props: any) => (props._sizeVariant === "small" ? "32px" : "39px")};
                    height: ${(props: any) => (props._sizeVariant === "small" ? "28px" : "36px")};
                    position: relative;
                    top: 0;
                    left: 0;
                    margin-right: 0;
                    background-color: ${getColor("common.white")};

                    svg {
                        color: ${getColor("primary")};
                        margin: auto;
                        width: ${(props: any) => (props._sizeVariant === "small" ? "20px" : "24px")};
                        height: ${(props: any) => (props._sizeVariant === "small" ? "20px" : "24px")};
                    }
                }
                &.loading {
                    > span:not(.atc-label) {
                        svg {
                            width: ${(props: any) => (props._sizeVariant === "small" ? "18px" : "20px")};
                            height: ${(props: any) => (props._sizeVariant === "small" ? "18px" : "20px")};
                        }
                    }
                }
                > span.atc-label {
                    width: ${(props: any) =>
                props._sizeVariant === "small" ? "calc(100% - 32px)" : "calc(100% - 39px)"};
                    text-align: center;
                    font-weight: normal;
                    letter-spacing: 0.3px;
                    font-size: ${(props: any) => (props._sizeVariant === "small" ? "16px" : "18px")};
                    line-height: ${(props: any) => (props._sizeVariant === "small" ? "20px" : "22px")};
                }
            }

            &:disabled {
                opacity: 0.5;
                border: 2px solid;
                &,
                &:hover {
                    background: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
            }
        `,
    },
    asLink: {
        css: css`
            &:hover,
            &:focus {
                .as-link-icon {
                    color: ${getColor("text.main")};
                }

                .as-link-text {
                    text-decoration: underline;
                }
            }
        `,
    },
    asLinkIconTextWrapper: {
        css: css`
            display: flex;
            align-items: center;
            line-height: 1;
        `,
    },
    asLinkIcon: {
        size: 13,
        color: "primary.main",
        css: css`
            transition: all 200ms ease-in-out;
        `,
    },
    asLinkText: {
        color: "text.main",
        size: "14px",
        lineHeight: "10px",
        css: css`
            margin-left: 6.5px;
            transition: all 200ms ease-in-out;
        `,
    },
};

interface AddToCartStatusInfoInterface {
    label: string;
    icon: NamedExoticComponent | string;
}

interface AddToCartStatusInterface {
    default: AddToCartStatusInfoInterface;
    loading: AddToCartStatusInfoInterface;
    success: AddToCartStatusInfoInterface;
}

const addToCartStatus: AddToCartStatusInterface = {
    default: {
        label: "Add to Cart",
        icon: ShoppingCart,
    },
    loading: {
        label: "Add to Cart",
        icon: "",
    },
    success: {
        label: "Success",
        icon: Check,
    },
};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, properties, allowZeroPricing, quoteRequired, minimumOrderQty },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    extendedAdditionalStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    iconOnly,
    alwaysVisible,
    asLink,
    withIcon,
    iconColor,
    subscription,
    onAddToCartClick,
    ...otherProps
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productAddToCartButtonStyles, extendedAdditionalStyles));
    const toasterContext: any = React.useContext(ToasterContext);
    const [asButtonStyles] = React.useState(() => mergeToNew(styles.asButton, extendedStyles));
    const [status, setStatus] = React.useState(addToCartStatus.default);
    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(properties?.unitOfMeasureData || "[]");

    useEffect(() => {
        let timeout: any;
        if (status === addToCartStatus.success) {
            timeout = setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 3000);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [status]);
    if (!alwaysVisible && ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart)) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        // if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
        //     toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
        //     return;
        // }

        setStatus(addToCartStatus.loading);
        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        let cartLine: any;
        // parent case: there is multiple unit of measures
        if (unitOfMeasuresData && unitOfMeasure) {
            const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
            // secondary case - root 1: the selected uom is not the default
            if (selectedUOM.length) {
                const minQty = parseFloat(selectedUOM[0].MinimumOrderQuantity) || 1;
                if (qtyOrdered < minQty) {
                    cartLine = (await addToCart({
                        productId,
                        qtyOrdered: minQty,
                        unitOfMeasure,
                        notes,
                        sectionOptions,
                        subscription,
                    })) as CartLineModel;
                    // root 1 - part 2: if the qty ordered is bigger than minQty for the selected alternative UOM
                } else {
                    cartLine = (await addToCart({
                        productId,
                        qtyOrdered,
                        unitOfMeasure,
                        notes,
                        sectionOptions,
                    })) as CartLineModel;
                } // root 2: the default uom is selected then use the returned data from productInfo
            } else {
                const minQty = parseFloat(properties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString());

                cartLine = (await addToCart({
                    productId,
                    qtyOrdered: qtyOrdered < minQty || !qtyOrdered ? minQty : qtyOrdered,
                    unitOfMeasure,
                    notes,
                    sectionOptions,
                })) as CartLineModel;
            }
            // root 3: no multiple uom then use returned data from productInfo
        } else {
            const minQty = parseFloat(properties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString());

            cartLine = (await addToCart({
                productId,
                qtyOrdered: qtyOrdered < minQty || !qtyOrdered ? minQty : qtyOrdered,
                unitOfMeasure,
                notes,
                sectionOptions,
            })) as CartLineModel;
        }

        setStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartLine.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    const isDisabled =
        isAddToCartButtonDisabled ||
        status !== addToCartStatus.default ||
        (alwaysVisible && ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart))
        
    if (iconOnly) {
        return (
            <Button
                {...asButtonStyles}
                onClick={addToCartClickHandler}
                className="button-wrapper-icon-only"
                disabled={isDisabled}
                {...otherProps}
            >
                {status === addToCartStatus.loading ? (
                    <LoadingSpinner size={22} color="primary" />
                ) : (
                    <Icon src={status.icon} size={22} color={(iconColor || getColor("common.white")) as any} />
                )}
            </Button>
        );
    }

    if (asLink) {
        return (
            <Clickable disabled={isDisabled} onClick={addToCartClickHandler} {...styles.asLink}>
                {status === addToCartStatus.loading ? (
                    <LoadingSpinner size={22} color="primary" />
                ) : (
                    <StyledWrapper {...styles.asLinkIconTextWrapper}>
                        {withIcon && <Icon src={status.icon} {...styles.asLinkIcon} className="as-link-icon" />}
                        <Typography {...styles.asLinkText} className="as-link-text">
                            {labelOverride ?? translate(status.label)}
                        </Typography>
                    </StyledWrapper>
                )}
            </Clickable>
        );
    }

    return (
        <Button
            icon={{ src: status.icon, position: "left" }}
            {...asButtonStyles}
            onClick={addToCartClickHandler}
            disabled={isDisabled}
            {...otherProps}
            className={status === addToCartStatus.loading ? "loading" : ""}
        >
            {status === addToCartStatus.loading ? (
                <Typography as="span">
                    <LoadingSpinner
                        size={15}
                        color="primary"
                        css={css`
                            margin-top: 5px !important;
                        `}
                    />
                </Typography>
            ) : null}
            <Typography className="atc-label" as="span">
                {labelOverride ?? translate(status.label)}
            </Typography>
        </Button>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
